import React, { useState, useEffect } from "react";
import { Machine } from "../../types/machine.type";
import { Produce } from "../../types/produce.type";
import {
  Typography,
  Select,
  Space,
  Input,
  Spin,
  Table,
  message,
  Form,
  Modal,
  Button,
  Menu,
  Dropdown,
} from "antd";
import useGetRequest from "../../hooks/useGetRequest";
import { api } from "../../api/api";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Machines() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [unitFilter, setUnitFilter] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [produceFilter, setProduceFilter] = useState<string | undefined>(
    undefined
  );
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [form] = Form.useForm();
  const { data: producesList, loading: listLoading } =
    useGetRequest(`/produce/list`);

  useEffect(() => {
    fetchMachines();
  }, [
    page,
    searchTerm,
    unitFilter,
    produceFilter,
    sortBy,
    sortOrder,
    pageSize,
  ]);

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/machine`, {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          sortBy,
          order: sortOrder,
          unitProduction: unitFilter,
          produceId: produceFilter,
        },
      });
      setMachines(response.data.machines);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching machines", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddMachine = () => {
    form.resetFields();
    setSelectedMachine(null);
    setIsModalOpen(true);
  };

  const handleSubmitForm = async (values: any) => {
    setConfirmLoading(true);
    const formattedValues = {
      ...values,
      produces: values.produces?.map((produceId: string) => ({
        produceId: produceId,
      })),
    };

    try {
      if (selectedMachine) {
        await api.put(`/machine/${selectedMachine._id}`, formattedValues);
        message.success("Станок обновлен.");
      } else {
        await api.post(`/machine`, formattedValues);
        message.success("Станок добавлен.");
      }
      fetchMachines();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving machine:", error);
      message.error(error.response.data.error);
    }
  };

  const handleEditMachine = (machine: Machine) => {
    const producesArray =
      machine.produces?.map((produce) => produce.produceId) || [];

    form.setFieldsValue({
      ...machine,
      produces: producesArray,
    });

    setSelectedMachine(machine);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Производимое",
      key: "produces",
      render: (_: any, record: Machine) => (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            maxWidth: "900px",
          }}
        >
          {record.produces.map((produce, index) => (
            <p key={produce.produceId}>
              {index + 1}.{produce.produceName}
            </p>
          ))}
        </div>
      ),
    },
    {
      title: "Произведено всего",
      dataIndex: "totalProduced",
      key: "totalProduced",
    },
    {
      title: "Произведено днем",
      dataIndex: "totalProducedDay",
      key: "totalProducedDay",
    },
    {
      title: "Произведено ночью",
      dataIndex: "totalProducedNight",
      key: "totalProducedNight",
    },
    {
      title: "Кол-во отходов",
      dataIndex: "wasteQuantity",
      key: "wasteQuantity",
      render: (value: number | undefined) => value ?? <i>Нет данных</i>,
    },
    {
      title: "",
      key: "actions",
      render: (_: any, machine: Machine) => {
        const menu = (
          <Menu>
            {authState.user?.role === "admin" && (
              <Menu.Item onClick={() => handleEditMachine(machine)}>
                Изменить
              </Menu.Item>
            )}
            {authState.user?.role !== UserRole.Accountant && (
              <>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${machine._id}/${machine.name}/machine/salary-reports`
                    )
                  }
                >
                  Смотреть отчеты
                </Menu.Item>

                <Menu.Item
                  onClick={() =>
                    navigate(`/${machine._id}/${machine.name}/profit-logs`)
                  }
                >
                  Смотреть прибыль
                </Menu.Item>
              </>
            )}
            <Menu.Item
              onClick={() =>
                navigate(`/${machine._id}/machine/${machine.name}/workers`)
              }
            >
              Смотреть сотрудников
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Станки</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />

        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="producedQuantity">Произведено</Option>
          <Option value="createdAt">Дата</Option>
          <Option value="wasteQuantity">Количество отходов</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        {authState.user?.role === "admin" && (
          <Button type="primary" onClick={handleAddMachine}>
            Добавить
          </Button>
        )}
      </Space>
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={machines}
          rowKey={(record) => record._id}
          pagination={{
            current: page,
            total,
            pageSize,
            onChange: (page, size) => {
              setPage(page);
              if (size !== pageSize) setPageSize(size);
            },
          }}
          scroll={{ x: "max-content" }}
        />
      )}
      <Modal
        title={selectedMachine ? "Изменить станок" : "Добавить станок"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="produces"
            label="Производимое"
            rules={[{ required: true, message: "Выберите продукции" }]}
          >
            <Select
              mode="multiple"
              placeholder="Выберите продукции"
              loading={listLoading}
            >
              {producesList?.map((produce: Produce) => (
                <Option key={produce._id} value={produce._id}>
                  {produce.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
